import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, Button, Select, MenuItem,
    FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Chip, Stack, Divider
} from '@mui/material';
import axios from 'axios';
import Navbar from '../setup/Navbar';
import '../../services/GlobalVariables';

/**
 * Item Changes Audit Report
 * Shows complete audit trail of item quantity changes (increases, decreases, cancellations)
 * Provides summary statistics and filtering options
 */

export default function ItemChangesAuditReport() {
    const [changes, setChanges] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    
    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [changeType, setChangeType] = useState('');
    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);
    
    // Fetch summary on component mount
    useEffect(() => {
        fetchSummary();
    }, []);
    
    const fetchSummary = async () => {
        try {
            const params = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            
            const response = await axios.get(`${global.SET_API_URL}itemChangesAudit/getSummary`, { params });
            if (response.data.success) {
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };
    
    const fetchChanges = async () => {
        setLoading(true);
        try {
            const params = {
                limit,
                offset
            };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (changeType) params.changeType = changeType;
            
            const response = await axios.get(`${global.SET_API_URL}itemChangesAudit/getAllChanges`, { params });
            if (response.data.success) {
                setChanges(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching changes:', error);
            alert('Error loading changes data');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSearch = () => {
        setOffset(0);
        fetchChanges();
        fetchSummary();
    };
    
    const getChangeTypeColor = (type) => {
        switch(type) {
            case 'INCREASE': return 'success';
            case 'DECREASE': return 'warning';
            case 'CANCEL': return 'error';
            default: return 'default';
        }
    };
    
    return (
        <>
            <Navbar />
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                    📊 Item Changes Audit Report
                </Typography>
                
                {/* Summary Cards */}
                {summary && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {summary.byType.map((stat) => (
                            <Grid item xs={12} md={4} key={stat.changeType}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" color="text.secondary">
                                            {stat.changeType}
                                        </Typography>
                                        <Typography variant="h4">
                                            {stat.changeCount}
                                        </Typography>
                                        <Typography variant="body2">
                                            Total Quantity: {stat.totalQuantity}
                                        </Typography>
                                        {stat.changeType !== 'INCREASE' && (
                                            <Typography variant="body2" color="error">
                                                Waste: {stat.totalWaste} pcs
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
                
                {/* Top Changed Items */}
                {summary && summary.topChangedItems.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                🔥 Most Changed Items
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item Name</TableCell>
                                            <TableCell align="right">Changes</TableCell>
                                            <TableCell align="right">Increased</TableCell>
                                            <TableCell align="right">Decreased</TableCell>
                                            <TableCell align="right">Cancelled</TableCell>
                                            <TableCell align="right">Total Waste</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {summary.topChangedItems.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.itemName}</TableCell>
                                                <TableCell align="right">{item.changeCount}</TableCell>
                                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                                    +{item.totalIncreased}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'warning.main' }}>
                                                    -{item.totalDecreased}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                                    ✕{item.totalCancelled}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                                                    {item.totalWaste}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}
                
                {/* Filters */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            🔍 Filters
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Change Type</InputLabel>
                                    <Select
                                        value={changeType}
                                        label="Change Type"
                                        onChange={(e) => setChangeType(e.target.value)}
                                    >
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="INCREASE">Increase</MenuItem>
                                        <MenuItem value="DECREASE">Decrease</MenuItem>
                                        <MenuItem value="CANCEL">Cancel</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Stack direction="row" spacing={1}>
                                    <Button 
                                        variant="contained" 
                                        onClick={handleSearch}
                                        disabled={loading}
                                        fullWidth
                                    >
                                        {loading ? 'Loading...' : 'Search'}
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                
                {/* Changes Table */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            📋 Change History
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date/Time</TableCell>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>Item Name</TableCell>
                                        <TableCell align="center">Type</TableCell>
                                        <TableCell align="right">Old Qty</TableCell>
                                        <TableCell align="right">New Qty</TableCell>
                                        <TableCell align="right">Change</TableCell>
                                        <TableCell align="right">KOT Printed</TableCell>
                                        <TableCell align="right">Waste</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Reason</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {changes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center">
                                                No changes found. Click Search to load data.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        changes.map((change) => (
                                            <TableRow key={change.id}>
                                                <TableCell>
                                                    {new Date(change.createdAt).toLocaleString()}
                                                </TableCell>
                                                <TableCell>{change.orderId}</TableCell>
                                                <TableCell>{change.itemName}</TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={change.changeType} 
                                                        color={getChangeTypeColor(change.changeType)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{change.oldQuantity}</TableCell>
                                                <TableCell align="right">{change.newQuantity}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                    {change.changeType === 'INCREASE' ? '+' : '-'}
                                                    {change.changeAmount}
                                                </TableCell>
                                                <TableCell align="right">{change.kotPrintedQuantity}</TableCell>
                                                <TableCell align="right" sx={{ color: change.wasteQuantity > 0 ? 'error.main' : 'inherit' }}>
                                                    {change.wasteQuantity}
                                                </TableCell>
                                                <TableCell>{change.userName || 'N/A'}</TableCell>
                                                <TableCell>{change.reason || '-'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {/* Pagination */}
                        {changes.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">
                                    Showing {offset + 1} to {offset + changes.length} entries
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => {
                                            setOffset(Math.max(0, offset - limit));
                                            fetchChanges();
                                        }}
                                        disabled={offset === 0}
                                    >
                                        Previous
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => {
                                            setOffset(offset + limit);
                                            fetchChanges();
                                        }}
                                        disabled={changes.length < limit}
                                    >
                                        Next
                                    </Button>
                                </Stack>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </>
    );
}
